// EL PLACEHOLDER QUE SE ESCRIBE SOLO — sólo en la caja de la home
// (ADR-2026-09-21-el-placeholder-de-la-home-propone-ideas).
//
// La caja vacía de la home tenía un placeholder que ENUMERABA ("fotos de tu producto, un
// video para redes, ideas para esta semana…"). Dice de qué se le puede hablar al agente,
// pero no muestra CÓMO se le habla: quien nunca escribió un pedido igual se queda mirando
// la caja. Acá el placeholder se escribe solo, idea por idea, y cada una es un pedido
// entero y copiable tal cual.
//
// ── POR QUÉ ESCRIBE EN EL DOM Y NO EN UN `useState` ───────────────────────
// Un estado que cambia cada ~55ms re-renderizaría `CajaDelAgenteHome` —y con ella
// `BarraEntradaChat` entera, que es el menú del "+", los selectores, el dictado y el
// botón de enviar— trece veces por segundo con la home quieta. Acá el efecto escribe
// `textarea.placeholder` derecho y no hay ni un render de más.
//
// Eso es seguro porque React NO reescribe una prop que no cambió: mientras `placeholder`
// siga valiendo lo mismo entre renders, el atributo que escribimos sobrevive. Si alguna
// vez cambia de verdad (cambiar de variante, enganchar un lote de fotos), React lo pisa y
// el siguiente tic lo vuelve a tomar — por eso `activo` se apaga con el lote de fotos,
// que tiene placeholder propio.
//
// Con `prefers-reduced-motion` no se escribe nada y queda el placeholder quieto de
// siempre, que sigue diciendo lo que la caja hace.
import { useEffect, type RefObject } from 'react';

/**
 * Las ideas que se tipean. Son PEDIDOS ENTEROS en voseo, no rubros ni categorías: lo que
 * hay que mostrar es la forma de hablarle al agente. Cortas a propósito —entran en un
 * renglón del teléfono, donde el placeholder se corta a mitad de palabra.
 *
 * Son otras que las de los pills de sugerencia (`SUGERENCIAS_AGENTE`): esas se pueden
 * tocar y completan la caja, así que repetirlas acá sería decir lo mismo dos veces en la
 * misma pantalla. Estas son el abanico ancho —producto, video, redes, campaña, análisis—
 * para que la rotación no se lea como "esto hace fotos".
 *
 * TODAS TERMINAN EN PUNTOS SUSPENSIVOS, con el mismo `…` de un solo carácter que usa el
 * placeholder quieto de la barra ("Preguntále lo que quieras a Atlas…") y el resto de la
 * app — no tres puntos sueltos, que a este tamaño se separan y se leen como un error de
 * tipeo. Cierran la frase igual que el placeholder de siempre, así que el momento en que
 * el ciclo pasa de una a otra no cambia de forma; y dicen que la idea sigue: es un pedido
 * para empezar, no una frase terminada.
 */
export const IDEAS_DEL_PLACEHOLDER = [
  'Sacale fotos a mi producto con fondo blanco…',
  'Hacé un video de 10 segundos para Instagram…',
  'Dame ideas de contenido para esta semana…',
  'Mostrá mi producto sobre una mesa de mármol…',
  'Armá una promo para el fin de semana…',
  'Analizá a mi competencia y decime qué hago…',
] as const;

/**
 * El placeholder QUIETO de la caja de la home: la primera idea de la rotación.
 *
 * Acá había una frase aparte que ENUMERABA ("Pedile lo que quieras: fotos de tu
 * producto, un video para redes, ideas para esta semana…") y que era la que se veía en
 * reposo, al arrancar cada vuelta y al cortarse el ciclo. Se fue por pedido del usuario
 * (ADR-2026-09-21-el-placeholder-quieto-es-una-idea-mas): decía de QUÉ se le puede
 * hablar al agente, que es justo lo que las ideas ya muestran —y mejor, porque muestran
 * CÓMO—, así que era un renglón de más en el único lugar de la caja donde se puede decir
 * una cosa sola.
 *
 * Que sea una idea y no vacío no es un detalle: este es el texto que se ve con
 * `prefers-reduced-motion`, donde no se escribe nada, y el que se repone cuando el ciclo
 * se corta a mitad de una frase. Vacío, esas dos situaciones dejarían una caja muda.
 */
export const PLACEHOLDER_QUIETO: string = IDEAS_DEL_PLACEHOLDER[0];

// Ritmos en ms. Borrar es ~la mitad que escribir porque al ojo le alcanza con ver que se
// deshace; si va al mismo ritmo, el ciclo se siente eterno. Son los mismos criterios que
// `VerboTipeado`, con números propios: acá las frases son diez veces más largas que un
// verbo, así que escribir va más rápido y la pausa con la frase entera es más corta (si
// no, una vuelta completa se va a un minuto y nadie la ve entera).
const MS_ESCRIBIR = 38;
const MS_BORRAR = 16;
const MS_PAUSA_COMPLETA = 2300; // frase entera en pantalla, antes de borrar
const MS_PAUSA_VACIA = 380; // vacío, antes de escribir la siguiente

/**
 * Escribe y borra `ideas` dentro del `textarea` que haya adentro de `contenedorRef`.
 *
 * @param contenedorRef El envoltorio de la barra de entrada. Se busca el `textarea`
 *   adentro en vez de pedir su ref: la barra no la expone, y un prop nuevo en ella sería
 *   una prop de la home metida en el componente que usan también el panel y la pantalla.
 * @param activo Se apaga cuando el placeholder no se ve o no es nuestro (hay borrador
 *   escrito, hay un lote de fotos enganchado). Apagado, se repone el quieto.
 * @param placeholderQuieto El de siempre. Es la PRIMERA frase del ciclo —se la ve un
 *   momento y se borra— para que la caja no arranque vacía, y vuelve en cada vuelta.
 */
export function usePlaceholderQueSeEscribe(
  contenedorRef: RefObject<HTMLElement | null>,
  ideas: readonly string[],
  activo: boolean,
  placeholderQuieto: string,
) {
  useEffect(() => {
    const el = contenedorRef.current?.querySelector('textarea');
    if (!el) return;
    if (!activo || ideas.length === 0) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // El quieto encabeza el ciclo, SALVO que ya sea la primera idea —que es el caso
    // desde que el quieto es una idea más—: prependerlo dejaría la misma frase dos veces
    // seguidas al empezar cada vuelta.
    const frases = placeholderQuieto === ideas[0] ? [...ideas] : [placeholderQuieto, ...ideas];
    // Arranca con el placeholder quieto ENTERO y en fase de borrado: la caja se dibuja
    // diciendo lo que dice siempre y recién después empieza a proponer.
    let indice = 0;
    let largo = placeholderQuieto.length;
    let escribiendo = false;
    let t = 0;

    const paso = () => {
      const frase = frases[indice % frases.length];
      let demora: number;
      if (escribiendo) {
        if (largo < frase.length) {
          largo += 1;
          demora = MS_ESCRIBIR;
        } else {
          escribiendo = false;
          demora = MS_PAUSA_COMPLETA;
        }
      } else if (largo > 0) {
        largo -= 1;
        demora = MS_BORRAR;
      } else {
        indice += 1;
        escribiendo = true;
        demora = MS_PAUSA_VACIA;
      }
      el.placeholder = frases[indice % frases.length].slice(0, largo);
      t = window.setTimeout(paso, demora);
    };

    t = window.setTimeout(paso, MS_PAUSA_COMPLETA);
    return () => {
      window.clearTimeout(t);
      // Se repone el quieto: el efecto se corta a mitad de una frase (al desmontar, o al
      // escribir la primera letra) y dejar "Hacé un vid" colgado sería peor que nada.
      el.placeholder = placeholderQuieto;
    };
  }, [contenedorRef, ideas, activo, placeholderQuieto]);
}
